import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock, ArrowLeft } from "lucide-react";
import { SITE } from "@/lib/site";
import { getPost, getRelatedPosts } from "@/lib/blog";
import useSEO from "@/hooks/useSEO";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });

function renderBlock(b, i) {
  switch (b.type) {
    case "h2": return <h2 key={i} className="font-display text-2xl md:text-3xl mt-12 mb-4 tracking-tight">{b.text}</h2>;
    case "h3": return <h3 key={i} className="font-display text-xl md:text-2xl mt-10 mb-3 tracking-tight">{b.text}</h3>;
    case "list": return (
      <ul key={i} className="space-y-2 my-5">
        {b.items.map((it, j) => (
          <li key={j} className="text-white/70 leading-relaxed flex gap-3"><span className="text-gold">→</span>{it}</li>
        ))}
      </ul>
    );
    case "callout": return (
      <div key={i} className="my-8 p-6 border-l-2 border-gold bg-ink-900">
        <p className="text-white/85 font-display text-lg italic leading-relaxed">{b.text}</p>
      </div>
    );
    case "image": return (
      <figure key={i} className="my-8">
        <img src={b.src} alt={b.alt} loading="lazy" className="w-full aspect-[16/9] object-cover" />
        {b.caption && <figcaption className="text-white/45 text-sm mt-3">{b.caption}</figcaption>}
      </figure>
    );
    case "p":
    default:
      return <p key={i} className="text-white/70 leading-relaxed my-4">{b.text}</p>;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  useSEO({
    title: post?.title,
    description: post?.excerpt,
    path: `/blog/${slug}`,
    image: post?.image,
    type: "article",
  });

  useEffect(() => {
    if (!post) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = `article-jsonld-${slug}`;
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.image.startsWith("http") ? post.image : `${SITE.url}${post.image}`,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: SITE.name, url: SITE.url },
      publisher: {
        "@type": "Organization",
        name: SITE.name,
        logo: { "@type": "ImageObject", url: `${SITE.url}/img/logo.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}/blog/${slug}` },
      keywords: post.tags.join(", "),
    });
    document.head.appendChild(el);
    // BreadcrumbList — Home › Blog › <this post>. Google shows this as a
    // rich path in search results and lifts CTR on blog snippets.
    const bc = document.createElement("script");
    bc.type = "application/ld+json";
    bc.id = `article-breadcrumb-jsonld-${slug}`;
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE.url}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${SITE.url}/blog/${slug}` },
      ],
    });
    document.head.appendChild(bc);
    return () => { el.remove(); bc.remove(); };
  }, [post, slug]);

  if (!post) return <Navigate to="/blog" replace />;
  const related = getRelatedPosts(slug);

  return (
    <>
      <article className="pt-36 pb-20 bg-ink-950">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-white/55 hover:text-gold" data-testid="back-to-blog">
            <ArrowLeft size={13}/> All articles
          </Link>

          <div className="mt-8 flex flex-wrap gap-2">
            {post.tags.map(t => (
              <span key={t} className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/30 px-3 py-1">{t}</span>
            ))}
          </div>

          <h1 className="font-display text-4xl md:text-6xl mt-6 tracking-tight leading-[1.05]">{post.title}</h1>

          <div className="mt-6 flex items-center gap-4 text-[12px] uppercase tracking-[0.18em] text-white/50">
            <span className="flex items-center gap-2"><Calendar size={12}/> {fmtDate(post.date)}</span>
            <span className="text-white/20">·</span>
            <span className="flex items-center gap-2"><Clock size={12}/> {post.readMinutes} min read</span>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-12">
          <img src={post.image} alt={`${post.title} — Toronto Custom Signs & Wraps`} className="w-full aspect-[16/9] object-cover" />
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-12 prose-content">
          <p className="text-white/85 text-lg leading-relaxed font-display italic border-l-2 border-gold pl-5">{post.excerpt}</p>
          <div className="mt-10">
            {post.content.map(renderBlock)}
          </div>

          <div className="mt-16 p-8 border border-white/10 bg-ink-900 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div>
              <div className="eyebrow">Free quote · 24-hour response</div>
              <h3 className="font-display text-2xl mt-2">Talk to our team about your project.</h3>
            </div>
            <Link to="/contact" className="btn-gold whitespace-nowrap" data-testid="blog-cta-quote">Get a quote <ArrowUpRight size={14}/></Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-20 bg-ink-900 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6">
            <div className="eyebrow">Related reading</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight">More from the studio</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {related.map(r => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="card-dark p-7 group flex flex-col gap-3" data-testid={`related-post-${r.slug}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{r.tags[0]}</div>
                  <h3 className="font-display text-xl text-white group-hover:text-gold transition-colors">{r.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{r.excerpt}</p>
                  <div className="mt-2 flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-white/60 group-hover:text-gold transition-colors">
                    Read <ArrowUpRight size={13}/>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
